import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import HomePage from './pages/HomePage';
import ReportPage from './pages/ReportPage';
import CustomersPage from './pages/CustomersPage';
import MerchantsPage from './pages/MerchantsPage';
import CouriersPage from './pages/CouriersPage';
import PricingPage from './pages/PricingPage';
import ContactPage from './pages/ContactPage';
import SubscribeSuccessPage from './pages/SubscribeSuccessPage';
import MerchantCasePage from './pages/MerchantCasePage';
import VerifyPage from './pages/VerifyPage';
import FeedbackPage from './pages/FeedbackPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import SetPasswordPage from './pages/SetPasswordPage';
import PilotSignupPage from './pages/PilotSignupPage';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import CaseManagement from './pages/admin/CaseManagement';
import CaseDetail from './pages/admin/CaseDetail';
import MerchantManagement from './pages/admin/MerchantManagement';
import MerchantDetail from './pages/admin/MerchantDetail';
import PricingManagement from './pages/admin/PricingManagement';
import CourierIntelligence from './pages/admin/CourierIntelligence';
import CourierAccountManagement from './pages/admin/CourierAccountManagement';
import SubscriptionManagement from './pages/admin/SubscriptionManagement';
import TransactionManagement from './pages/admin/TransactionManagement';
import FeedbackManagement from './pages/admin/FeedbackManagement';
import ContactManagement from './pages/admin/ContactManagement';
import AcquisitionTracking from './pages/admin/AcquisitionTracking';
import AdminLayout from './components/admin/AdminLayout';
import MerchantLayout from './components/merchant/MerchantLayout';
import MerchantLogin from './pages/merchant/MerchantLogin';
import MerchantDashboard from './pages/merchant/MerchantDashboard';
import MerchantCases from './pages/merchant/MerchantCases';
import MerchantCaseDetail from './pages/merchant/MerchantCaseDetail';
import MerchantTeam from './pages/merchant/MerchantTeam';
import MerchantAccount from './pages/merchant/MerchantAccount';
import CourierLayout from './components/courier/CourierLayout';
import CourierLogin from './pages/courier/CourierLogin';
import CourierDashboard from './pages/courier/CourierDashboard';
import CourierCases from './pages/courier/CourierCases';
import CourierMerchants from './pages/courier/CourierMerchants';
import CourierHotspots from './pages/courier/CourierHotspots';
import CourierAccount from './pages/courier/CourierAccount';
import Layout from './components/Layout';
import ScrollToTop from './components/ScrollToTop';
import { AdminOverlaysProvider } from './components/admin/overlays';
import { checkSession } from './utils/session';

// Component to handle /admin route redirect
const AdminRedirect = () => {
  const isAuthenticated = checkSession('admin');

  return isAuthenticated ? <Navigate to="/admin/dashboard" replace /> : <Navigate to="/admin/login" replace />;
};

/**
 * /subscribe used to be a separate "buy a plan" page. Registration and plan
 * choice now happen together on /start-pilot, so this only forwards — the
 * search is carried over so an old `?plan=merchant` link still arrives with
 * that plan pre-selected. /subscribe/success is untouched: it is the Stripe
 * return URL for plan checkout.
 */
const SubscribeRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/start-pilot${search}`} replace />;
};

function App() {
  return (
    <Router>
      <ScrollToTop />
      {/* Top-level toast stack: lets a page fire a toast right before
          navigating (e.g. "thanks for your feedback" from FeedbackPage,
          then on to Home) and have it still be visible after the route
          changes, since this provider sits above the Routes swap instead of
          inside any one route. Routes that nest their own provider (admin,
          merchant, courier, /report, /merchant/case/:token) keep using that
          nearer one — this one only backstops routes with none of their own. */}
      <AdminOverlaysProvider>
      <Routes>
        <Route path="/" element={<Layout><HomePage /></Layout>} />
        <Route path="/report" element={<Layout><ReportPage /></Layout>} />
        <Route path="/customers" element={<Layout><CustomersPage /></Layout>} />
        <Route path="/merchants" element={<Layout><MerchantsPage /></Layout>} />
        <Route path="/couriers" element={<Layout><CouriersPage /></Layout>} />
        <Route path="/pricing" element={<Layout><PricingPage /></Layout>} />
        <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
        {/* Two halves of one flow: the form, then the link from its email. */}
        {/* Merchant pilot signup. The /start-pilot paths are kept because
            confirmation links already sent by email point at them. */}
        <Route path="/start-pilot" element={<Layout><PilotSignupPage audience="merchant" /></Layout>} />
        <Route path="/start-pilot/:token" element={<Layout><PilotSignupPage audience="merchant" /></Layout>} />
        <Route path="/couriers/start-pilot" element={<Layout><PilotSignupPage audience="courier" /></Layout>} />
        <Route path="/couriers/start-pilot/:token" element={<Layout><PilotSignupPage audience="courier" /></Layout>} />
        <Route path="/subscribe" element={<SubscribeRedirect />} />
        <Route path="/subscribe/success" element={<Layout><SubscribeSuccessPage /></Layout>} />
        {/* Stripe's return URL for a courier Intelligence checkout — the same
            page, pointed at the courier confirm endpoint and portal. */}
        <Route
          path="/couriers/subscribe/success"
          element={<Layout><SubscribeSuccessPage audience="courier" /></Layout>}
        />

        {/* Magic-link case review — no login, distinct from the /merchant/cases
            portal route below. Wrapped in its own toast provider: this route
            sits outside both Layout and MerchantLayout, so without this,
            success/failure feedback here had nowhere to render but an inline
            page banner. */}
        <Route
          path="/merchant/case/:token"
          element={
            <AdminOverlaysProvider>
              <MerchantCasePage />
            </AdminOverlaysProvider>
          }
        />

        <Route path="/verify" element={<Layout><VerifyPage /></Layout>} />
        <Route path="/feedback" element={<Layout><FeedbackPage /></Layout>} />

        {/* Invitations and password resets — used by admins and merchant users
            alike, so they sit outside both panels. */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/set-password/:token" element={<SetPasswordPage />} />

        {/* Merchant portal */}
        <Route path="/merchant/login" element={<MerchantLogin />} />
        <Route element={<MerchantLayout />}>
          <Route path="/merchant/dashboard" element={<MerchantDashboard />} />
          <Route path="/merchant/cases" element={<MerchantCases />} />
          {/* Signed-in case review. Distinct from /merchant/case/:token above:
              same content, but reached with a session rather than a magic link
              and wrapped in the portal shell. */}
          <Route path="/merchant/cases/:caseId" element={<MerchantCaseDetail />} />
          <Route path="/merchant/team" element={<MerchantTeam />} />
          <Route path="/merchant/account" element={<MerchantAccount />} />
        </Route>

        {/* Courier portal */}
        <Route path="/courier/login" element={<CourierLogin />} />
        <Route element={<CourierLayout />}>
          <Route path="/courier/dashboard" element={<CourierDashboard />} />
          <Route path="/courier/cases" element={<CourierCases />} />
          <Route path="/courier/merchants" element={<CourierMerchants />} />
          <Route path="/courier/hotspots" element={<CourierHotspots />} />
          <Route path="/courier/account" element={<CourierAccount />} />
        </Route>

        <Route path="/admin" element={<AdminRedirect />} />
        <Route path="/admin/login" element={<AdminLogin />} />

        {/* Every route inside AdminLayout is session-guarded and gets the
            sidebar + topbar shell. */}
        <Route element={<AdminLayout />}>
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/cases" element={<CaseManagement />} />
          <Route path="/admin/cases/:caseId" element={<CaseDetail />} />
          <Route path="/admin/merchants" element={<MerchantManagement />} />
          <Route path="/admin/merchants/:id" element={<MerchantDetail />} />
          <Route path="/admin/pricing" element={<PricingManagement />} />
          <Route path="/admin/courier-intelligence" element={<CourierIntelligence />} />
          <Route path="/admin/courier-accounts" element={<CourierAccountManagement />} />
          <Route path="/admin/subscriptions" element={<SubscriptionManagement />} />
          <Route path="/admin/transactions" element={<TransactionManagement />} />
          <Route path="/admin/feedback" element={<FeedbackManagement />} />
          <Route path="/admin/contact-requests" element={<ContactManagement />} />
          <Route path="/admin/acquisition" element={<AcquisitionTracking />} />
        </Route>
      </Routes>
      </AdminOverlaysProvider>
    </Router>
  );
}

export default App;
